import { z } from 'zod';
import type { operations } from '@molchanovs/api-contract';
import { api } from '@shared/api';

const requestIdSchema = z.uuid();
type InspectionResponse =
  operations['getApiV1ShopifyStockSyncRunsByRequestIdReconciliation']['responses'][200]['content']['application/json'];
type AcknowledgementResponse =
  operations['postApiV1ShopifyStockSyncRunsByRequestIdReconcile']['responses'][200]['content']['application/json'];
type CommandBody =
  operations['postApiV1ShopifyStockSyncRunsByRequestIdReconcile']['requestBody']['content']['application/json'];
const etagSchema = z.string().regex(/^"shopify-stock-recovery-v1-[a-f0-9]{64}"$/u);
export const stockRecoveryInspectionSchema = z.object({
  data: z.object({
    requestId: requestIdSchema,
    status: z.string().max(100),
    eligible: z.boolean(),
    reason: z.string().max(100).nullable(),
    locationId: z.string().max(200).nullable(),
    inventoryItemId: z.string().max(200).nullable(),
    executionCount: z.number().int().min(0).max(101),
    intentCount: z.number().int().min(0).max(1001),
    historyComplete: z.boolean(),
    recovered: z.boolean(),
    evidenceDigest: z.string().regex(/^[a-f0-9]{64}$/u),
  }),
}) satisfies z.ZodType<InspectionResponse>;
const acknowledgementSchema = z.object({
  data: z.object({ requestId: requestIdSchema, resolution: z.literal('stopped_without_dispatch') }),
}) satisfies z.ZodType<AcknowledgementResponse>;
const commandSchema = z.strictObject({
  requestId: requestIdSchema,
  etag: etagSchema,
  idempotencyKey: z
    .string()
    .min(1)
    .max(200)
    .refine((value) => value.trim() === value),
});
export type StockRecoveryInspection = InspectionResponse['data'] & {
  etag: string;
};
export type StockRecoveryCommand = z.infer<typeof commandSchema>;

export async function inspectStockRecovery(
  requestId: string,
  signal: AbortSignal
): Promise<StockRecoveryInspection> {
  const id = requestIdSchema.parse(requestId);
  const response = await api.getWithMetadata(
    `/api/v1/shopify/stock-sync/runs/${id}/reconciliation`,
    { signal, schema: stockRecoveryInspectionSchema }
  );
  const data = stockRecoveryInspectionSchema.parse(response.data).data;
  const etag = etagSchema.parse(response.headers.get('etag'));
  if (data.requestId !== id) throw new Error('Invalid recovery evidence identity');
  return { ...data, etag };
}

export async function stopStockRequestWithoutDispatch(input: StockRecoveryCommand) {
  const command = commandSchema.parse(input);
  const result = acknowledgementSchema.parse(
    await api.post(
      `/api/v1/shopify/stock-sync/runs/${command.requestId}/reconcile`,
      { resolution: 'stop_without_dispatch' } satisfies CommandBody,
      { headers: { 'If-Match': command.etag, 'Idempotency-Key': command.idempotencyKey } }
    )
  ).data;
  if (result.requestId !== command.requestId)
    throw new Error('Invalid recovery acknowledgement identity');
  return result;
}

export const stockRecoveryKeys = {
  inspection: (viewer: string, requestId: string) =>
    ['shopify-stock-recovery', viewer, requestId] as const,
  attempt: (viewer: string, requestId: string) =>
    ['shopify-stock-recovery-attempt', viewer, requestId] as const,
};
